/**
 * /transit/invite/:token — a single-use link from an admin: join the org
 * (name and a password) or set a new password (a reset). Accepting signs the
 * user in.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Button, Input, PasswordInput } from '../../components/ui'
import { transitAuth } from '../../services/transit'
import type { InviteLink } from '../../types/transit'
import { AuthCard, FormError, SystemNotice } from './TransitSignIn'

export default function TransitInvite() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const [link, setLink] = useState<InviteLink | null>(null)
  const [linkError, setLinkError] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    transitAuth.openLink(token).then(setLink).catch((e) => setLinkError(e.message))
  }, [token])

  if (!link) {
    return (
      <AuthCard title="Transit">
        {linkError
          ? <FormError error={`${linkError}. Ask your org's admin for a new link.`} />
          : <div className="flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>}
      </AuthCard>
    )
  }

  const reset = link.kind === 'reset'
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (password !== confirm) {
      setError('The passwords don’t match')
      return
    }
    setLoading(true)
    try {
      await transitAuth.acceptLink(token, name, password)
      navigate('/transit', { replace: true })
    } catch (err: any) {
      setError(err.message || 'That didn’t work')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthCard title={reset ? `Set a new password for ${link.email}` : `Join ${link.org} on Transit as ${link.email}`}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          {!reset && (
            <Input id="invite-name" label="Your name" autoComplete="name" value={name}
              onChange={(e) => setName(e.target.value)} required disabled={loading} className="h-11" />
          )}
          <PasswordInput id="invite-password" label="Password (12 characters or more)" autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} required minLength={12} maxLength={128} disabled={loading} className="h-11" />
          <PasswordInput id="invite-confirm" label="Confirm password" autoComplete="new-password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} required disabled={loading} className="h-11" />
        </div>
        <FormError error={error} />
        <SystemNotice acknowledged={acknowledged} onChange={setAcknowledged} />
        <Button type="submit" size="lg" className="h-11 w-full text-base" isLoading={loading} disabled={loading || !acknowledged}>
          {reset ? 'Set password and sign in' : 'Join and sign in'}
        </Button>
        <p className="text-center text-xs text-muted-foreground">This link works once and expires {new Date(link.expires_at).toLocaleString()}.</p>
      </form>
    </AuthCard>
  )
}
